import type { HseReportType } from '@felix/domain';
import { useShell } from '../../shell-context.js';
import { hsePath } from '../hse/routes.js';
import { DefectList } from './DefectList.js';
import { DefectReport } from './DefectReport.js';
import { type DefectsRoute, defectsPath } from './routes.js';
import type { DefectsSource } from './source.js';

export interface DefectsScreenProps {
  source: DefectsSource;
  /** Sorgu dizesinden (`defectsRoute`). */
  route: DefectsRoute;
}

/**
 * Maintenance (PMS) ▸ Defects (ADR 0060 §11; ADR 0096): liste, yeni arıza ve LGS-17 kaydı. Görünüm yolun
 * sorgu dizesinden gelir; ekran gemi uygulamasında ve web ofiste aynıdır (ADR 0019 §2).
 */
export function DefectsScreen({ source, route }: DefectsScreenProps) {
  const shell = useShell();
  const go = (next: DefectsRoute) => shell?.navigate(defectsPath(next));
  if (route.view === 'new' || route.view === 'detail') {
    const id = route.view === 'detail' ? route.id : null;
    return (
      <DefectReport
        key={id ?? 'new'}
        source={source}
        id={id}
        equipmentId={route.view === 'new' ? route.equipmentId : null}
        dailyReportId={route.view === 'new' ? route.dailyReportId : null}
        hseReportId={route.view === 'new' ? (route.hseReportId ?? null) : null}
        workOrderId={route.view === 'new' ? (route.workOrderId ?? null) : null}
        onBack={() => go({ view: 'list' })}
        onOpen={(next) => go({ view: 'detail', id: next })}
        onHse={() => shell?.navigate('/compliance/deficiency?new=1')}
        // Bağlı kayıtlar ve hızlı işlemler (ADR 0096 §3–§6); yol sözleşmesi HSEQ ile ortak
        onWorkOrder={(workOrderId, vesselId) =>
          shell?.navigate(
            `/pms/work-orders?wo=${workOrderId}${source.scope === 'office' && vesselId ? `&vessel=${vesselId}` : ''}`,
          )
        }
        onNonConformity={(link) =>
          shell?.navigate(
            hsePath(link.reportType as HseReportType, { view: 'detail', id: link.id, tab: null }),
          )
        }
        onRequisition={(requisitionId) =>
          shell?.navigate(`/purchasing/requisitions?id=${requisitionId}`)
        }
        onNewRequisition={(defect) =>
          shell?.navigate(
            `/purchasing/requisitions?${new URLSearchParams({ new: '1', defect: defect.id, ref: defect.defectNo })}`,
          )
        }
        onNewNonConformity={(defect) =>
          shell?.navigate(`/compliance/non-conformity?new=1&defect=${defect.id}`)
        }
        onDaily={(report, day) =>
          shell?.navigate(
            `/operations/daily-reports?report=${report}${day ? `&from=${day}&to=${day}` : ''}`,
          )
        }
      />
    );
  }
  return (
    <DefectList
      source={source}
      onOpen={(id) => go({ view: 'detail', id })}
      onNew={() => go({ view: 'new', equipmentId: null, dailyReportId: null })}
    />
  );
}
